import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { InterfaceMode, ROLE_RANK, Role, toInterfaceMode } from '@smm/types'
import { authApi } from '@/api/endpoints.js'
import { setAccessToken, setAuthFailureHandler } from '@/api/client.js'

/**
 * Authentication state.
 *
 * The access token deliberately does **not** live in this store's reactive
 * state — it is held in `api/client.js`, in a plain module variable. Pinia
 * state can end up in devtools, in an SSR payload, or in a persistence plugin
 * someone adds later; a token in any of those places is a token that outlived
 * the tab it belonged to.
 *
 * What lives here is the *user*, which is not a secret, plus the flags the
 * router and layout need.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const isAuthenticated = computed(() => user.value !== null)
  /** True until the initial session restore finishes — the router waits on it. */
  const isInitializing = ref(true)
  const isLoading = ref(false)

  const role = computed(() => user.value?.role ?? null)
  const isStaff = computed(() => (ROLE_RANK[role.value] ?? -1) >= ROLE_RANK[Role.SUPPORT])
  const isAdmin = computed(() => (ROLE_RANK[role.value] ?? -1) >= ROLE_RANK[Role.ADMIN])
  const isSuperAdmin = computed(() => role.value === Role.SUPER_ADMIN)
  // An account with no address is not "unverified" — it is inside its grace
  // period, which the removal banner handles instead. Conflating the two
  // would show a "verify your email" prompt with no email to verify.
  const needsEmailVerification = computed(() => Boolean(user.value?.email) && !user.value.emailVerified)

  const displayName = computed(() => user.value?.fullName || user.value?.username || 'Account')

  /**
   * Which interface this account is served — Normal or Pro.
   *
   * It lives on the user because that is where the server keeps it, and
   * because arriving with the session restore is what stops the shell from
   * rendering one mode and swapping to the other a moment later. Anything
   * unrecognised, including a signed-out `null`, reads as Normal: a customer
   * who has never chosen must not land in the dense interface.
   *
   * Switching is `useInterfaceMode()`; this is the read side, kept here so the
   * router can consult it without pulling in the save logic.
   */
  const interfaceMode = computed(() => toInterfaceMode(user.value?.interfaceMode))
  const isProMode = computed(() => interfaceMode.value === InterfaceMode.PRO)

  /**
   * Accounts created the fast way have no email, so they carry a deadline
   * after which they are removed. These drive the in-panel warning — which is
   * the *only* channel such an account has, since there is nowhere to email.
   */
  const hasEmail = computed(() => Boolean(user.value?.email))
  const emailDeadline = computed(() =>
    user.value?.emailDeadline ? new Date(user.value.emailDeadline) : null,
  )
  const needsEmail = computed(() => isAuthenticated.value && !hasEmail.value)

  const daysUntilRemoval = computed(() => {
    if (!emailDeadline.value) return null
    return Math.max(0, Math.ceil((emailDeadline.value.getTime() - Date.now()) / 86_400_000))
  })

  /** Tightens the banner's tone as the deadline approaches. */
  const removalUrgency = computed(() => {
    const days = daysUntilRemoval.value
    if (days === null) return null
    if (days <= 1) return 'error'
    if (days <= 2) return 'warning'
    return 'info'
  })

  /** Attaches an email, which clears the deadline server-side. */
  async function setEmail(email) {
    const response = await authApi.setEmail(email)
    user.value = response.data
    return response
  }

  /** True when the user's role is at least `minimum`. */
  function hasRole(minimum) {
    return (ROLE_RANK[role.value] ?? -1) >= (ROLE_RANK[minimum] ?? Infinity)
  }

  function applySession(payload) {
    setAccessToken(payload.accessToken)
    user.value = payload.user
  }

  function clearSession() {
    setAccessToken(null)
    user.value = null
  }

  /**
   * Restores a session on page load, using the refresh cookie.
   *
   * A failure here is the *normal* case for a signed-out visitor, so it
   * resolves quietly rather than throwing — the router treats a null user as
   * "not signed in", which is exactly right.
   */
  async function initialize() {
    isInitializing.value = true
    try {
      const session = await authApi.refresh()
      applySession(session)
    } catch {
      clearSession()
    } finally {
      isInitializing.value = false
    }
  }

  async function login(credentials) {
    isLoading.value = true
    try {
      const session = await authApi.login(credentials)
      applySession(session)
      return session.user
    } finally {
      isLoading.value = false
    }
  }

  async function register(input) {
    isLoading.value = true
    try {
      const session = await authApi.register(input)
      applySession(session)
      return session.user
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Sign in with a passkey. The server mints a challenge, the device signs
   * it, the server verifies and issues the same session a password would.
   * With `autofill` the browser offers the passkey inside the username
   * field's suggestions instead of a modal; a cancelled or unsupported
   * prompt resolves to null rather than throwing.
   */
  async function loginWithPasskey({ identifier = '', remember = false, autofill = false } = {}) {
    const { startAuthentication } = await import('@simplewebauthn/browser')
    const { data } = await authApi.passkeys.loginOptions({ identifier: identifier || undefined })
    let response
    try {
      response = await startAuthentication({ optionsJSON: data.options, useBrowserAutofill: autofill })
    } catch (error) {
      if (autofill || error?.name === 'NotAllowedError' || error?.name === 'AbortError') return null
      throw error
    }
    isLoading.value = true
    try {
      const session = await authApi.passkeys.loginVerify({
        challengeId: data.challengeId,
        response,
        remember,
      })
      applySession(session)
      return session.user
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    try {
      await authApi.logout()
    } catch {
      // A network failure must not leave someone stuck signed in locally.
    } finally {
      clearSession()
    }
  }

  async function refreshUser() {
    if (!isAuthenticated.value) return null
    const { user: fresh } = await authApi.me()
    user.value = fresh
    return fresh
  }

  /** Applied after a profile edit, so the header updates without a refetch. */
  function patchUser(changes) {
    if (user.value) user.value = { ...user.value, ...changes }
  }

  // When the client exhausts its refresh attempts, drop the local session so
  // the router can send the user to the login page.
  setAuthFailureHandler(clearSession)

  return {
    user,
    isAuthenticated,
    isInitializing,
    isLoading,
    role,
    isStaff,
    isAdmin,
    isSuperAdmin,
    needsEmailVerification,
    displayName,
    interfaceMode,
    isProMode,
    hasRole,
    hasEmail,
    needsEmail,
    emailDeadline,
    daysUntilRemoval,
    removalUrgency,
    setEmail,
    initialize,
    login,
    loginWithPasskey,
    register,
    logout,
    refreshUser,
    patchUser,
    clearSession,
  }
})
